import { isEmailBlast } from "@utils/userSegment";
import { DiscountInfo, Plan } from "src/utils/redux/slices/plans";
import { PromoCodeErrorType } from "src/utils/redux/slices/promoCode";
import i18n from "src/locales";
import { PromoCodeApplicationProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/order_operation";

/**
 * Determine promo code is available in query string for blast users
 */
export const hasPromoCodeAvailable = () => {
  const promoCode =
    new URLSearchParams(window.location.search).get("display_promo") || null;
  return isEmailBlast() && !!promoCode;
};

/** Calculate promocode discount percentage relative to the actual plan price,
 * instead of relative to the inflated "regular" price. */
export const calculatePromoDiscountPercentage = (
  plan: Partial<Plan>,
  promoDiscountAmount?: number
) => {
  if (!promoDiscountAmount || !plan.plan_price) return 0;

  return Math.round((promoDiscountAmount / plan.plan_price) * 100);
};

/** Calculates price after a promo discount percentage is applied */
export const getPriceAfterDiscount = (
  price: number,
  discountPercentage: number
) => {
  return price * (1 - discountPercentage / 100);
};

/** Handles applying both types(fixed amount and percentage) of promo discounts to a plan */
export function calculateDiscountedPlanPrice(
  plan: Plan,
  discountInfo: DiscountInfo
) {
  if (!discountInfo) {
    return plan.plan_price;
  }
  if (discountInfo.discountType === "PERCENT") {
    return getPriceAfterDiscount(plan.plan_price, discountInfo.discountPercent);
  }
  return plan.plan_price - discountInfo.discountAmount;
}

export enum PromoCodeFailureReason {
  CodeNotFound = "CodeNotFound",
  CodeExpired = "CodeExpired",
  IneligiblePlanOrCurrency = "IneligiblePlanOrCurrency",
  CodeInactiveOrUsageLimitReached = "CodeInactiveOrUsageLimitReached",
  Unknown = "Unknown",
}

/**
 * Maps a promo code failure reason to the user-facing error copy.
 */
export function getPromoCodeApplicationError(
  failureReason: PromoCodeFailureReason
): { promoCodeError: string; promoCodeErrorType: PromoCodeErrorType } {
  switch (failureReason) {
    case PromoCodeFailureReason.CodeNotFound:
      return {
        promoCodeError: i18n.t("checkout:invalidCode"),
        promoCodeErrorType: "IncorrectPromoCode",
      };
    case PromoCodeFailureReason.CodeExpired:
      return {
        promoCodeError: i18n.t("checkout:expiredCode"),
        promoCodeErrorType: "PromoCodeRedemptionBlocked",
      };
    case PromoCodeFailureReason.IneligiblePlanOrCurrency:
      return {
        promoCodeError: i18n.t("checkout:errorPromo"),
        promoCodeErrorType: "PromoCodeRedemptionBlocked",
      };
    case PromoCodeFailureReason.CodeInactiveOrUsageLimitReached:
      return {
        promoCodeError: i18n.t("checkout:genericPromoError"),
        promoCodeErrorType: "PromoCodeRedemptionBlocked",
      };
    default:
      return {
        promoCodeError: i18n.t("checkout:genericPromoError"),
        promoCodeErrorType: "UnknownError",
      };
  }
}

/**
 * Maps a `PromoCodeApplicationProto` into a `PromoCodeFailureReason`.
 */
export function mapPromoCodeApplicationProtoToPromoCodeFailureReason(
  application?: PromoCodeApplicationProto
): PromoCodeFailureReason {
  switch (application?.failureReason) {
    case "CODE_NOT_FOUND":
      return PromoCodeFailureReason.CodeNotFound;
    case "CODE_EXPIRED":
      return PromoCodeFailureReason.CodeExpired;
    case "INELIGIBLE_PLAN":
    case "INELIGIBLE_CURRENCY":
      return PromoCodeFailureReason.IneligiblePlanOrCurrency;
    case "CODE_INACTIVE":
    case "USAGE_LIMIT_REACHED":
      return PromoCodeFailureReason.CodeInactiveOrUsageLimitReached;
    default:
      return PromoCodeFailureReason.Unknown;
  }
}

/**
 * Maps the HTTP status code returned by the legacy promo validation endpoint into
 * a `PromoCodeFailureReason`.
 */
export function mapHttpStatusToPromoCodeFailureReason(
  status?: number
): PromoCodeFailureReason {
  switch (status) {
    case 406:
      return PromoCodeFailureReason.CodeNotFound;
    case 410:
      return PromoCodeFailureReason.CodeExpired;
    default:
      return PromoCodeFailureReason.Unknown;
  }
}
